import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { LocationSelectModal } from "./Modal";
import DataSvc from "../../../services/dataSvc";

interface LocationSelectProps {
  selectedLocations: { id: number; name: string }[];
  setSelectedLocations: (locations: { id: number; name: string }[]) => void;
}

/**
 * Handles selecting multiple facilities from the pool of all facilities (for admin)
 * or all facilities for a specific organization (for org_admin).
 * Handles complex filtering and tracking selected facilities.
 */
export const LocationSelect: React.FC<LocationSelectProps> = ({
  selectedLocations,
  setSelectedLocations,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`hookForm.locationSelect.${key}`);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: locations, isLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: () =>
      DataSvc.getLocations({
        summary_mode: true,
        limit: 100000,
      }),
  });

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSetSelectedLocations = (
    locations: { id: number; name: string }[]
  ) => {
    setSelectedLocations(locations);
  };

  if (isLoading || !locations || locations.list.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="admin-location-select">
        <div className="admin-location-select-list">
          {selectedLocations.map((location) => (
            <div key={location.id} className="admin-location-select-item">
              {location.name}
            </div>
          ))}
        </div>
        <div className="admin-location-select-controls">
          <button
            className="btn btn-border"
            onClick={handleOpenModal}
            type="button"
          >
            {t("edit")}
          </button>
        </div>
      </div>
      {isModalOpen && (
        <LocationSelectModal
          allLocations={locations.list}
          selectedLocations={selectedLocations}
          setSelectedLocations={handleSetSelectedLocations}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};
